import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppRouter } from './router';
import { useAppSelector } from './hooks/redux';
import {
  validateSession,
  fetchRolesFromAccessToken,
  fetchUserProfile,
  setEntraAccount,
  setTokenExpiredWithFailedRefresh,
  serializeAccount,
} from './store/slices/authSlice';
import { getMsalInstance, getEntraUserInfo } from './config/authConfig';
import { clearAuthRedirectInProgress } from './utils/authRedirect';
import { usePageTracking } from './hooks/usePageTracking';
import { useTokenRefresh } from './hooks/useTokenRefresh';
import { useSessionRecovery } from './hooks/useSessionRecovery';
import { TokenExpirationModal } from './components/common/TokenExpirationModal';
import { ImpersonationBanner } from './components/impersonation';
import { NotificationToast } from './components/common/NotificationToast';
import { clearChunkReloadFlag } from './utils/lazyWithRetry';
import type { AppDispatch } from './store';
import { logger, setLogUserContextProvider } from './utils/logger';

/**
 * Main Application Component
 *
 * Responsibilities:
 * - Handle MSAL redirect callback (must be called ONCE per app load)
 * - Initialize application state
 * - Validate user session on mount
 * - Render the router
 * - Handle global app-level concerns
 * - Track page views automatically
 *
 * Note: MSAL is already initialized in index.tsx before this component renders.
 */
const App: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { authReadyState, tokenExpiredWithFailedRefresh, userInfo } = useAppSelector(
    (state) => state.auth
  );
  const [redirectHandled, setRedirectHandled] = useState(false);

  // Enable automatic page view tracking
  usePageTracking();

  // Enable proactive token refresh (checks every 60 seconds)
  useTokenRefresh();

  // Enable session recovery on tab visibility change (detects expired sessions
  // when user returns to a stale tab after a long idle period)
  useSessionRecovery();

  // Clear the chunk-reload flag once the app has successfully bootstrapped.
  // This prevents the lazyWithRetry utility from thinking a reload already happened
  // on the next chunk failure (which could be caused by a future deployment).
  useEffect(() => {
    clearChunkReloadFlag();
  }, []);

  useEffect(() => {
    setLogUserContextProvider(() => ({
      userId: userInfo?.userId,
      tenantId: userInfo?.userId,
    }));
    if (userInfo?.roles?.length) {
      sessionStorage.setItem('creatoros_user_roles', JSON.stringify(userInfo.roles));
    }
  }, [userInfo?.userId, userInfo?.roles]);

  /**
   * Handle token expiration modal acknowledgment
   * Saves current path and navigates to login page
   */
  const handleTokenExpiredAcknowledge = useCallback(() => {
    logger.info('User acknowledged token expiration, redirecting to login');

    // Clear the expiration flag
    dispatch(setTokenExpiredWithFailedRefresh(false));

    // Save current path for redirect after login
    sessionStorage.setItem('auth_redirect_uri', window.location.pathname);

    // Navigate to root - ProtectedRoute will redirect to Entra
    navigate('/');
  }, [dispatch, navigate]);

  useEffect(() => {
    const handleMsalRedirect = async () => {
      try {
        // Get already-initialized MSAL instance (initialized in index.tsx)
        const msalInstance = getMsalInstance();

        // Handle MSAL redirect callback (MUST be called only once per page load)
        const response = await msalInstance.handleRedirectPromise();

        const isOnCallbackPage = window.location.pathname === '/auth/callback';

        if (response && response.account) {
          // Immediately save the account to Redux - don't wait for validateSession
          const userInfo = getEntraUserInfo(response.account);

          if (userInfo) {
            dispatch(
              setEntraAccount({
                account: serializeAccount(response.account),
                userInfo,
              })
            );

            // Set the freshly authenticated account as active in MSAL
            // This ensures acquireTokenSilent uses the correct account and
            // its fresh refresh token rather than stale cached credentials
            msalInstance.setActiveAccount(response.account);

            // Clear any OTHER stale accounts from a previous session
            // that could cause acquireTokenSilent to use invalid refresh tokens
            const allAccounts = msalInstance.getAllAccounts();
            for (const acc of allAccounts) {
              if (acc.homeAccountId !== response.account.homeAccountId) {
                logger.info('[App] Clearing stale MSAL account', { username: acc.username });
                msalInstance.clearCache({ account: acc });
              }
            }

            // Reset redirect state to allow future redirects if needed (e.g., after logout)
            clearAuthRedirectInProgress();
          }
        } else if (isOnCallbackPage) {
          // We're on /auth/callback but handleRedirectPromise returned null.
          // This means the redirect flow failed (e.g., account deleted from directory,
          // expired auth code, etc.). Clear any stale cached accounts/tokens to prevent
          // acquireTokenSilent from using invalid cached credentials.
          const staleAccounts = msalInstance.getAllAccounts();
          if (staleAccounts.length > 0) {
            logger.warn(
              '[App] On auth callback but no redirect response - clearing stale MSAL cache'
            );
            for (const acc of staleAccounts) {
              msalInstance.clearCache({ account: acc });
            }
          }
          clearAuthRedirectInProgress();
        } else {
          // Check if there are any accounts already in MSAL cache
          const accounts = msalInstance.getAllAccounts();
          if (accounts.length > 0) {
            // Reset redirect state since we have an existing session
            clearAuthRedirectInProgress();
          }
        }

        // Validate session after redirect is handled (also handles existing sessions)
        await dispatch(validateSession());

        // CRITICAL: Validate user status with backend (triggers suspended/deleted check)
        // This ensures backend validates user is not suspended before allowing access
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          const profileResult = await dispatch(fetchUserProfile());

          // CRITICAL: Extract roles from access token (Entra External ID fix)
          // Entra External ID doesn't include app roles in ID tokens, only in access tokens
          const rolesResult = await dispatch(fetchRolesFromAccessToken());

          // If both API token acquisitions failed with interaction_required,
          // OIDC auth succeeded but we can't get API access tokens.
          // This happens when the user's Entra account is deleted or API permissions
          // are revoked. Clear the stale MSAL cache and treat as unauthenticated.
          const profileFailed =
            profileResult.meta?.requestStatus === 'rejected' &&
            profileResult.payload === 'interaction_required';
          const rolesFailed =
            rolesResult.meta?.requestStatus === 'rejected' &&
            rolesResult.payload === 'interaction_required';

          if (profileFailed && rolesFailed) {
            logger.warn('[App] API token acquisition failed after redirect - clearing auth state');
            const staleAccounts = msalInstance.getAllAccounts();
            for (const acc of staleAccounts) {
              msalInstance.clearCache({ account: acc });
            }
            // Re-validate session with cleared cache → will set isAuthenticated: false
            await dispatch(validateSession());
          }
        }

        setRedirectHandled(true);
      } catch (error) {
        logger.error('[App] Error handling MSAL redirect:', error);
        setRedirectHandled(true);
        // Still validate session even if redirect handling fails
        dispatch(validateSession());
      }
    };

    handleMsalRedirect();
  }, [dispatch]); // Removed 'roles' to prevent infinite loop

  // Show loading screen until auth is fully ready
  // authReadyState is the single source of truth - blocks until 'ready' or 'unauthenticated'
  const showLoading =
    !redirectHandled ||
    authReadyState === 'initializing' ||
    authReadyState === 'authenticated_pending_roles';

  if (showLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
          <p className="text-lg text-gray-600">
            {authReadyState === 'authenticated_pending_roles'
              ? 'Loading your dashboard...'
              : 'Getting things ready...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Impersonation Banner - Shows at the very top when admin is impersonating */}
      <ImpersonationBanner />

      {/* Token Expiration Modal */}
      <TokenExpirationModal
        isOpen={tokenExpiredWithFailedRefresh}
        onSignInClick={handleTokenExpiredAcknowledge}
      />

      {/* Main App Router */}
      <AppRouter />

      {/* Global notification toasts — rendered above all modals (z-[70]) */}
      <NotificationToast />
    </>
  );
};

export default App;
